import { useId, useRef, useState, type PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import type { Entry } from '../../api/types'
import { useDateFormat } from '../../i18n/useDateFormat'
import { cx } from '../../lib/cx'
import { AmountText } from '../AmountDisplay'
import { Button } from '../Button'
import { Icon, type IconName } from '../Icon'
import { toast } from '../toastStore'
import { StatusChip } from './StatusChip'

export interface EntryRowProps {
  entry: Entry
  /** Her confirmation state; defaults to the entry's own. */
  confirmation?: Entry['confirmation']
  onConfirm?: () => void
  onQuery?: () => void
  /** Officers: swipe left (or use the Reverse button) to reverse it, with a reason and a second officer. */
  onReverse?: () => void
  /** Where the row and a reversal's original link to. Default /app/activity/:id; null shows them without links
   * (officers reading a member's entries, whose receipt screen is written for her own money). */
  href?: ((entryId: string) => string) | null
  /** A confirm or query is being sent. */
  busy?: boolean
  className?: string
}

const KIND_ICON: Record<string, IconName> = {
  savings: 'savings',
  repayment: 'payments',
  disbursement: 'account_balance_wallet',
  interest: 'trending_up',
  close: 'fact_check',
  penalty: 'warning',
  fee: 'receipt_long',
  charge_payment: 'payments',
  levy: 'groups',
  payout: 'account_balance_wallet',
  bank_charge: 'account_balance',
  suspense: 'help',
  match: 'compare_arrows',
  transfer: 'swap_horiz',
  opening: 'flag',
  rollover: 'sync',
  offset: 'swap_horiz',
  waiver: 'gavel',
  reversal: 'undo',
  adjustment: 'edit_note',
}

const LONG_PRESS_MS = 550
const SWIPE_REVEAL_PX = 64

function copyReference(reference: string, copied: string) {
  void navigator.clipboard?.writeText(reference).then(
    () => toast.show({ message: copied, tone: 'success', duration: 2500 }),
    () => undefined,
  )
}

/**
 * One ledger entry as it touched her (PRD 17): what happened, the amount, who recorded it
 * and who confirmed it, so trust is not taken on faith. A reversal links to its original.
 * Long-press copies the reference; officers swipe left to reveal Reverse.
 */
export function EntryRow({ entry, confirmation = entry.confirmation, onConfirm, onQuery, onReverse,
  href = (id) => `/app/activity/${id}`, busy = false, className }: EntryRowProps) {
  const { t } = useTranslation()
  const { formatDay } = useDateFormat()
  const [revealed, setRevealed] = useState(false)
  // Each row's buttons are described by its own title and reference, so "It's right" is never ambiguous.
  const titleId = useId()
  const metaId = useId()
  const about = `${titleId} ${metaId}`
  const press = useRef<{ x: number; timer: ReturnType<typeof setTimeout> | null; long: boolean } | null>(null)

  const onPointerDown = (event: PointerEvent) => {
    const timer = setTimeout(() => {
      if (press.current) press.current.long = true
      copyReference(entry.ref, t('money.entry.copied', { ref: entry.ref }))
    }, LONG_PRESS_MS)
    press.current = { x: event.clientX, timer, long: false }
  }
  const endPress = (event: PointerEvent) => {
    const current = press.current
    if (!current) return
    if (current.timer) clearTimeout(current.timer)
    const dx = event.clientX - current.x
    if (onReverse && dx < -SWIPE_REVEAL_PX) setRevealed(true)
    else if (onReverse && dx > SWIPE_REVEAL_PX) setRevealed(false)
    press.current = null
  }
  const cancelPress = () => {
    if (press.current?.timer) clearTimeout(press.current.timer)
    press.current = null
  }

  const amount = entry.value_change !== 0
    ? <AmountText valueNgwee={entry.value_change} tone={entry.value_change > 0 ? 'positive' : 'neutral'} />
    : entry.owed_change > 0
      ? <AmountText valueNgwee={entry.owed_change} tone="owed" />
      : entry.paid_off > 0
        ? <span className="entry-row__paid">{t('money.entry.paidOff')} <AmountText valueNgwee={entry.paid_off} /></span>
        : <AmountText valueNgwee={entry.lines.reduce((sum, line) => sum + Math.abs(line.amount), 0) / 2} />

  return (
    <article className={cx('entry-row', revealed && 'entry-row--revealed', className)} aria-label={entry.kind_label}>
      <div className="entry-row__main" onPointerDown={onPointerDown} onPointerUp={endPress}
        onPointerCancel={cancelPress} onPointerLeave={cancelPress}
        onContextMenu={(event) => press.current?.long && event.preventDefault()}>
        <span className={cx('entry-row__icon', `entry-row__icon--${entry.kind}`)}>
          <Icon name={KIND_ICON[entry.kind] ?? 'receipt_long'} />
        </span>
        <div className="entry-row__body">
          {href ? (
            <Link to={href(entry.id)} className="entry-row__title" id={titleId}>
              {entry.kind_label}
            </Link>
          ) : (
            <span className="entry-row__title" id={titleId}>{entry.kind_label}</span>
          )}
          {entry.memo && <p className="entry-row__memo">{entry.memo}</p>}
          <p className="entry-row__meta" id={metaId}>
            {formatDay(entry.effective_date)} · <span className="tabular">{entry.ref}</span>
          </p>
          <p className="entry-row__witnesses">
            <span>{t('money.entry.recordedBy', { who: entry.recorded_by })}</span>
            <span>{entry.confirmed_by ? t('money.entry.confirmedBy', { who: entry.confirmed_by })
              : t('money.entry.notConfirmed')}</span>
          </p>
          {entry.reverses && (
            <p className="entry-row__link">
              <Icon name="undo" size={16} />
              {entry.reverses_id && href ? (
                <Link to={href(entry.reverses_id)}>{t('money.entry.reverses', { ref: entry.reverses })}</Link>
              ) : t('money.entry.reverses', { ref: entry.reverses })}
            </p>
          )}
          {entry.reversed_by && (
            <p className="entry-row__link">
              <Icon name="undo" size={16} />
              {entry.reversed_by_id && href ? (
                <Link to={href(entry.reversed_by_id)}>{t('money.entry.reversedBy', { ref: entry.reversed_by })}</Link>
              ) : t('money.entry.reversedBy', { ref: entry.reversed_by })}
            </p>
          )}
        </div>
        <div className="entry-row__side">
          <span className="entry-row__amount">{amount}</span>
          {confirmation === 'confirmed' && <StatusChip tone="success">{t('money.entry.confirmed')}</StatusChip>}
          {confirmation === 'queried' && <StatusChip tone="danger" icon="flag">{t('money.entry.queried')}</StatusChip>}
          {confirmation === 'pending' && <StatusChip tone="warning">{t('money.entry.pending')}</StatusChip>}
        </div>
      </div>
      {confirmation === 'pending' && (onConfirm || onQuery) && (
        <div className="entry-row__actions">
          {onConfirm && (
            <Button variant="secondary" icon="check" onClick={onConfirm} loading={busy} aria-describedby={about}>
              {t('money.entry.confirm')}
            </Button>
          )}
          {onQuery && (
            <Button variant="text" icon="flag" onClick={onQuery} disabled={busy} aria-describedby={about}>
              {t('money.entry.query')}
            </Button>
          )}
        </div>
      )}
      {onReverse && (
        <div className="entry-row__reverse">
          <Button variant="destructive" icon="undo" onClick={() => { setRevealed(false); onReverse() }}
            aria-label={t('money.entry.reverseLabel', { ref: entry.ref })}>
            {t('money.entry.reverse')}
          </Button>
        </div>
      )}
    </article>
  )
}
